import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  FileText,
  Trash2,
  Sparkles,
  TrendingUp,
  Search,
  FileSpreadsheet,
  FileType,
  Loader2,
  Pencil,
  Mail,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { calcTotals, fmtEUR, fmtDate } from "../lib/storage";
import { exportBudgetXLSX, exportBudgetDOCX } from "../lib/export";
import {
  listarPresupuestos,
  eliminarPresupuesto,
  enviarPresupuestoPorEmail,
} from "../lib/budgetsApi";
import { getBudgetPDFBase64 } from "../lib/pdf";
import { useAuth } from "../context/AuthContext";
import { validarEmail } from "../lib/validations";

export default function Dashboard() {
  const { account } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [q, setQ] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [emailModal, setEmailModal] = useState(null);

  const refresh = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await listarPresupuestos();
      setBudgets(data);
    } catch (err) {
      console.error("dashboard_load_error", err);
      setError(err?.response?.data?.error || "No se pudieron cargar los presupuestos");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, account?.email]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return budgets;
    return budgets.filter(
      (b) =>
        b.id.toLowerCase().includes(term) ||
        (b.cliente?.nombre || "").toLowerCase().includes(term)
    );
  }, [budgets, q]);

  const stats = useMemo(() => {
    const totals = budgets.map((b) => calcTotals(b.partidas).total);
    return {
      count: budgets.length,
      sum: totals.reduce((a, n) => a + n, 0),
      avg: budgets.length ? totals.reduce((a, n) => a + n, 0) / budgets.length : 0,
    };
  }, [budgets]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este presupuesto?")) return;
    try {
      await eliminarPresupuesto(id);
      toast.success("Presupuesto eliminado");
      refresh();
    } catch (err) {
      console.error("dashboard_delete_error", err);
      toast.error("No se pudo eliminar el presupuesto");
    }
  };

  const handleExport = async (budget, formato) => {
    if (!budget?.partidas?.length) {
      toast.error("Este presupuesto no tiene partidas todavía");
      return;
    }
    try {
      if (formato === "excel") {
        exportBudgetXLSX(budget);
      } else {
        await exportBudgetDOCX(budget);
      }
      toast.success(`Descargando ${formato === "excel" ? "Excel" : "Word"}`);
    } catch (err) {
      console.error("export_error", err);
      toast.error("No se pudo generar el archivo");
    }
  };

  return (
    <div className="px-10 py-10 max-w-7xl mx-auto" data-testid="dashboard">
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
            Reformas Express
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight" data-testid="dashboard-title">
            Tus presupuestos.
          </h1>
          <p className="text-slate-500 mt-2 max-w-xl">
            Crea, edita y exporta presupuestos de reforma con asistencia IA. Todo se guarda en este navegador (datos mock).
          </p>
        </div>
        <Link to="/nuevo" data-testid="dashboard-new-btn">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-5 rounded-md gap-2">
            <Plus className="w-4 h-4" /> Nuevo presupuesto
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10 mb-8">
        <StatCard label="Presupuestos" value={stats.count} icon={<FileText className="w-5 h-5" />} testid="stat-count" />
        <StatCard
          label="Volumen total"
          value={fmtEUR(stats.sum)}
          icon={<TrendingUp className="w-5 h-5" />}
          testid="stat-sum"
        />
        <StatCard
          label="Ticket medio"
          value={fmtEUR(stats.avg)}
          icon={<Sparkles className="w-5 h-5" />}
          testid="stat-avg"
        />
      </div>

      <div className="rx-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
          <h2 className="font-display font-bold text-lg">Listado</h2>
          <div className="relative w-72 max-w-full">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por ID o cliente…"
              className="pl-9 h-9"
              data-testid="dashboard-search"
            />
          </div>
        </div>

        {cargando ? (
          <div className="p-16 text-center" data-testid="dashboard-loading">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Cargando tus presupuestos…</p>
          </div>
        ) : error ? (
          <div className="p-16 text-center" data-testid="dashboard-error">
            <p className="text-red-600 text-sm mb-3">{error}</p>
            <Button onClick={refresh} variant="outline" className="border-slate-300">
              Reintentar
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="dashboard-empty">
            <div className="inline-flex p-4 rounded-full bg-blue-50 text-blue-600 mb-4">
              <FileText className="w-7 h-7" />
            </div>
            <h3 className="font-display font-bold text-xl mb-2">Sin presupuestos aún</h3>
            <p className="text-slate-500 mb-5 text-sm">
              Crea tu primer presupuesto en menos de 1 minuto con la asistencia IA.
            </p>
            <Link to="/nuevo">
              <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                <Sparkles className="w-4 h-4" /> Probar con IA
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((b) => {
              const total = calcTotals(b.partidas).total;
              return (
                <div
                  key={b.id}
                  className="px-5 py-4 grid grid-cols-12 gap-4 items-center hover:bg-slate-50/60 transition-colors"
                  data-testid={`budget-row-${b.id}`}
                >
                  <div className="col-span-3">
                    <div className="font-mono text-xs text-slate-400">{b.id}</div>
                    <div className="font-semibold text-slate-900 truncate">
                      {b.cliente?.nombre || "Sin cliente"}
                    </div>
                  </div>
                  <div className="col-span-2 text-sm text-slate-600 truncate">
                    {b.cliente?.direccion || "—"}
                  </div>
                  <div className="col-span-2 text-sm text-slate-500">{fmtDate(b.fecha)}</div>
                  <div className="col-span-1">
                    <span
                      className={`text-xs px-2 py-1 rounded-md font-semibold ${
                        b.estado === "final"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {b.estado === "final" ? "Final" : "Borrador"}
                    </span>
                  </div>
                  <div className="col-span-2 text-right font-display font-bold text-blue-600">
                    {fmtEUR(total)}
                  </div>
                  <div className="col-span-2 flex justify-end items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleExport(b, "excel")}
                      data-testid={`budget-export-excel-${b.id}`}
                      className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                      aria-label="Descargar Excel"
                      title="Descargar Excel"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExport(b, "word")}
                      data-testid={`budget-export-word-${b.id}`}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      aria-label="Descargar Word"
                      title="Descargar Word"
                    >
                      <FileType className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEmailModal(b)}
                      data-testid={`budget-email-${b.id}`}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      aria-label="Enviar por email"
                      title="Enviar por email al cliente"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <Link
                      to={`/presupuesto/${b.id}`}
                      data-testid={`budget-edit-${b.id}`}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      aria-label="Abrir presupuesto"
                      title="Abrir presupuesto"
                    >
                      <Pencil className="w-4 h-4" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(b.id)}
                      data-testid={`budget-delete-${b.id}`}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      aria-label="Eliminar"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SendEmailModal
        budget={emailModal}
        open={!!emailModal}
        onClose={() => setEmailModal(null)}
        onSent={refresh}
      />
    </div>
  );
}

function SendEmailModal({ budget, open, onClose, onSent }) {
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [adjuntar, setAdjuntar] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRecipient((budget?.cliente?.email || "").trim());
    setMessage("");
    setAdjuntar(true);
    setError(null);
  }, [open, budget]);

  const total = useMemo(
    () => (budget ? calcTotals(budget.partidas).total : 0),
    [budget],
  );

  const emailErr = useMemo(() => validarEmail(recipient, { obligatorio: true }), [recipient]);
  const sinPartidas = !budget?.partidas?.length;
  const valido = !emailErr && !sinPartidas;

  const handleEnviar = async () => {
    if (!budget || !valido || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const payload = {
        recipientEmail: recipient.trim().toLowerCase(),
        message: message.trim() || undefined,
        locale: "es",
      };
      if (adjuntar) {
        try {
          payload.pdfBase64 = getBudgetPDFBase64(budget);
        } catch (err) {
          console.error("pdf_build_error", err);
          toast.error("No se pudo generar el PDF para adjuntar");
          setEnviando(false);
          return;
        }
      }
      const res = await enviarPresupuestoPorEmail(budget.id, payload);
      toast.success(`Presupuesto enviado a ${res.sentTo}`);
      onSent?.();
      onClose();
    } catch (err) {
      console.error("send_email_error", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "No se pudo enviar el email";
      setError(msg);
      toast.error(msg);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="send-email-modal">
        <DialogHeader>
          <DialogTitle>Enviar presupuesto por email</DialogTitle>
        </DialogHeader>

        {budget ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-slate-500">{budget.id}</span>
              <span className="font-display font-bold text-blue-700 tabular-nums">
                {fmtEUR(total)}
              </span>
            </div>
            <div className="text-slate-700 truncate mt-0.5">
              {budget.cliente?.nombre || "Sin cliente"}
            </div>
          </div>
        ) : null}

        {sinPartidas ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Este presupuesto no tiene partidas todavía. Añade partidas antes de enviarlo.
          </div>
        ) : null}

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1.5 block">
              Email del destinatario *
            </Label>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              maxLength={120}
              data-testid="send-email-recipient"
              autoFocus
            />
            {emailErr && recipient.length > 0 ? (
              <p className="text-[11px] text-red-600 mt-1">{emailErr}</p>
            ) : null}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1.5 block">
              Mensaje opcional
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
              rows={3}
              maxLength={2000}
              placeholder="Añade una nota personal para tu cliente…"
              data-testid="send-email-message"
            />
            <div className="text-[11px] text-slate-400 mt-1 text-right">
              {message.length}/2000
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={adjuntar}
              onChange={(e) => setAdjuntar(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300"
              data-testid="send-email-attach-pdf"
            />
            Adjuntar PDF del presupuesto
          </label>
        </div>

        {error ? (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} className="border-slate-300">
            Cancelar
          </Button>
          <Button
            onClick={handleEnviar}
            disabled={!valido || enviando}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 gap-2"
            data-testid="send-email-submit"
          >
            {enviando ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Enviando…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Enviar
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, icon, testid }) {
  return (
    <div className="rx-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 text-slate-400">
        <span className="text-xs uppercase tracking-widest font-bold">{label}</span>
        {icon}
      </div>
      <div className="font-display font-bold text-2xl tracking-tight">{value}</div>
    </div>
  );
}
